<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'MiniCard',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    colorCode: {
      type: String,
      required: false,
    },
    badge: {
      type: Boolean,
      required: false,
    },
    noIcon: {
      type: Boolean,
      required: false,
    },
    noLink: {
      type: Boolean,
      required: false,
    },
    title: {
      type: String,
      required: false,
    },
    icon: {
      type: String,
      required: false,
    },
    text: {
      type: String,
      required: false,
    },
    linkTo: {
      type: Object,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);
  const b_7315092382398562_class = computed(() => [
    'app-minicard',
    props.badge
      ? `bg-${props.colorCode}-100 border border-${props.colorCode}-500`
      : null,
  ]);

  const $data = computed(() => ({
    ux: ux.value,
    b_7315092382398562_class: b_7315092382398562_class.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'ux' },
      { name: 'b_7315092382398562_class', nodeId: '7315092382398562' },
    ],
    loaders: {},
    ux: {},
    dataRefs: { ux, b_7315092382398562_class },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.colorCode,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('colorCode');
    }
  );
  watch(
    () => props.badge,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('badge');
    }
  );
  watch(
    () => props.noIcon,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('noIcon');
    }
  );
  watch(
    () => props.noLink,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('noLink');
    }
  );
  watch(
    () => props.title,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('title');
    }
  );
  watch(
    () => props.icon,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('icon');
    }
  );
  watch(
    () => props.text,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('text');
    }
  );
  watch(
    () => props.linkTo,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('linkTo');
    }
  );

  const actions = {};

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <component
    :is="badge ? 'ItpBadge' : 'div'"
    :class="b_7315092382398562_class"
    :noBoxShadow="!!badge"
  >
    <ItpBox alignItems="center">
      <ItpIcon :name="icon" :title="title" class="me-2" v-if="!noIcon">
      </ItpIcon>
      <ItpText strong :text="text" class="me-2"> </ItpText>
      <ItpLink
        :icon="$config.externalLinkIcon"
        :to="linkTo"
        v-if="!noLink && linkTo && linkTo.params && linkTo.params.id"
      >
      </ItpLink>
    </ItpBox>
  </component>
</template>
